/**
 * Clarity-only "Percentage of Equipment Cost" pricing.
 *
 * Single source of truth for:
 * - Catalogue gate (Clarity brand only)
 * - Visible equipment base (excludes services, % lines, discounts, etc.)
 * - Applying percentage line amounts
 *
 * Customer-facing surfaces show name + final $ only; staff may edit %.
 * D-Tools imported fixed service lines are unchanged unless they carry
 * pricingMethod = percentage_of_equipment (Portal-native).
 */
import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { filterVisibleDtoolsSystems } from "./dtoolsOptionVisibility";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import type { DtoolsItemBucket } from "./dtoolsSiJsonImport";
import type { DtoolsReviewPrimary, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { equipmentBaseAmountFromLine } from "./proposalHardwareMarkup";

export const EQUIPMENT_PRICING_METHOD_FIXED = "fixed" as const;
export const EQUIPMENT_PRICING_METHOD_PERCENTAGE = "percentage_of_equipment" as const;

export type EquipmentPricingMethod =
  | typeof EQUIPMENT_PRICING_METHOD_FIXED
  | typeof EQUIPMENT_PRICING_METHOD_PERCENTAGE;

export const CLARITY_BRAND_NORM = "clarity";

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

function moneyNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Ceiling to whole dollars for Clarity percentage-based service fees.
 * Uses a tiny epsilon so exact whole-dollar float results are not bumped up
 * (e.g. 349.0000000002 → 349, while 349.01 → 350).
 */
export function ceilClarityServiceFeeToWholeDollar(amount: number): number {
  const n = moneyNumber(amount);
  if (n <= 0) return 0;
  const EPS = 1e-9;
  return Math.ceil(n - EPS);
}

function clean(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

/** Case-insensitive Clarity brand gate. */
export function isClarityBrand(brand: string | null | undefined): boolean {
  return clean(brand).toLowerCase() === CLARITY_BRAND_NORM;
}

export function normalizeEquipmentPricingMethod(
  value: unknown,
): EquipmentPricingMethod {
  const raw = clean(value).toLowerCase();
  if (raw === EQUIPMENT_PRICING_METHOD_PERCENTAGE) return EQUIPMENT_PRICING_METHOD_PERCENTAGE;
  return EQUIPMENT_PRICING_METHOD_FIXED;
}

export function isEquipmentPercentagePricingMethod(
  value: unknown,
): boolean {
  return normalizeEquipmentPricingMethod(value) === EQUIPMENT_PRICING_METHOD_PERCENTAGE;
}

/** Percentage pricing is only valid for Clarity-branded products. */
export function isEquipmentPercentagePricingAllowed(input: {
  brand?: string | null;
  pricingMethod?: unknown;
}): boolean {
  if (!isEquipmentPercentagePricingMethod(input.pricingMethod)) return false;
  return isClarityBrand(input.brand);
}

export function normalizeDefaultPercentage(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 10000) / 10000;
}

/** Labour / service identity (same spirit as projectCatalogueEligibility LABOUR_IDENTITY_RE). */
const LABOUR_IDENTITY_RE =
  /^(?:clarity\s+)?(?:installation|install(?:ation)?(?:[\s,]*testing)?(?:\s*(?:&|and)\s*commissioning)?|commissioning(?:\s*(?:and|&)\s*tuning)?|programming|(?:savant\s+|lutron\s+|control\s+)?system\s+programming|labour|labor|service(?:\s*call)?|design\s*fees?|project\s*management(?:\s*fees?)?|delivery|freight|warranty(?:\s*extension)?|training|site\s*visit|travel|notes?|miscellaneous|misc\.?)\b/i;

const NON_EQUIPMENT_CATEGORY_RE =
  /\b(labor|labour|programming|commissioning|service|design\s*fee|project\s*management|delivery|freight|shipping|tax|discount|allowance|install(?:ation)?|notes?|miscellaneous|misc\.?)\b/i;

const SHIPPING_DELIVERY_RE = /\b(shipping|delivery|freight|courier|logistics)\b/i;
const TAX_RE = /\b(tax|gst|vat)\b/i;
const DISCOUNT_RE = /\b(discount|allowance|rebate|credit)\b/i;

export type EquipmentPricingLineFields = {
  brand?: string | null;
  manufacturer?: string | null;
  name?: string | null;
  model?: string | null;
  displayName?: string | null;
  productName?: string | null;
  description?: string | null;
  dtoolsDescription?: string | null;
  polishedDescription?: string | null;
  category?: string | null;
  bucket?: DtoolsItemBucket | string | null;
  pricingMethod?: unknown;
  percentageOfEquipment?: number | null;
  lineTotal?: number | null;
  unitPrice?: number | null;
  quantity?: number | null;
};

function lineBrand(line: EquipmentPricingLineFields): string {
  return clean(line.brand ?? line.manufacturer);
}

function lineIdentityBlob(line: EquipmentPricingLineFields): string {
  return [
    line.model,
    line.productName,
    line.name,
    line.displayName,
    line.dtoolsDescription,
    line.polishedDescription,
    line.description,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" ");
}

function looksLikeLabourOrServiceIdentity(line: EquipmentPricingLineFields): boolean {
  const fields = [
    clean(line.model),
    clean(line.productName),
    clean(line.name),
    clean(line.displayName),
  ].filter(Boolean);
  if (fields.some((v) => LABOUR_IDENTITY_RE.test(v))) return true;
  const blob = lineIdentityBlob(line);
  if (SHIPPING_DELIVERY_RE.test(blob) || TAX_RE.test(blob)) return true;
  if (DISCOUNT_RE.test(blob) && !/\bhardware\b/i.test(blob)) return true;
  const category = clean(line.category);
  if (NON_EQUIPMENT_CATEGORY_RE.test(category)) return true;
  return false;
}

/**
 * Lines that must never contribute to the percentage base
 * (prevents recursive % of % and non-equipment charges).
 */
export function isExcludedFromEquipmentBase(line: EquipmentPricingLineFields): boolean {
  if (isEquipmentPercentagePricingMethod(line.pricingMethod)) return true;
  const bucket = String(line.bucket ?? "").toLowerCase();
  if (bucket === "service" || bucket === "discount" || bucket === "cable") return true;
  if (bucket === "optional" || bucket === "non_billable") return true;
  if (looksLikeLabourOrServiceIdentity(line)) return true;
  return false;
}

/** True when this line should be priced via equipment percentage. */
export function isEquipmentPercentageLine(line: EquipmentPricingLineFields): boolean {
  return isEquipmentPercentagePricingAllowed({
    brand: lineBrand(line),
    pricingMethod: line.pricingMethod,
  });
}

export function computeEquipmentPercentageAmount(
  equipmentBase: number,
  percentage: number | null | undefined,
): number {
  const pct = normalizeDefaultPercentage(percentage);
  if (pct == null || pct <= 0) return 0;
  const base = Math.max(0, moneyNumber(equipmentBase));
  // Clarity % fees: calculate the full fee first, then round up to a whole dollar.
  // Avoid binary float bumps at exact whole-dollar boundaries via epsilon ceil.
  return ceilClarityServiceFeeToWholeDollar((base * pct) / 100);
}

export type FlatEquipmentLine = EquipmentPricingLineFields & {
  id?: string | number | null;
  key?: string | null;
  lineTotal: number;
  unitPrice?: number;
  baseUnitPrice?: number | null;
  quantity?: number;
};

/** Retail / flat proposal: sum equipment-eligible line totals (pre-markup base when stored). */
export function sumFlatVisibleEquipmentCost(lines: FlatEquipmentLine[]): number {
  let sum = 0;
  for (const line of lines) {
    if (isExcludedFromEquipmentBase(line)) continue;
    sum += Math.max(0, equipmentBaseAmountFromLine(line));
  }
  return roundMoney(sum);
}

function stagingLineToFields(line: DtoolsStagingLine): EquipmentPricingLineFields & {
  baseUnitPrice?: number | null;
} {
  return {
    brand: line.manufacturer,
    manufacturer: line.manufacturer,
    name: line.displayName ?? line.model,
    model: line.model,
    displayName: line.displayName,
    dtoolsDescription: line.dtoolsDescription,
    polishedDescription: line.polishedDescription,
    category: line.category,
    bucket: line.bucket,
    pricingMethod: (line as DtoolsStagingLine & { pricingMethod?: unknown }).pricingMethod,
    percentageOfEquipment: (line as DtoolsStagingLine & { percentageOfEquipment?: number | null })
      .percentageOfEquipment,
    lineTotal: line.lineTotal,
    unitPrice: line.unitPrice,
    baseUnitPrice: line.baseUnitPrice,
    quantity: line.quantity,
  };
}

function addPrimaryEquipmentTotal(block: DtoolsReviewPrimary, into: { sum: number }): void {
  const primary = stagingLineToFields(block.line);
  if (!isExcludedFromEquipmentBase(primary)) {
    into.sum += Math.max(0, equipmentBaseAmountFromLine(primary));
  }
  for (const acc of block.accessories) {
    const fields = stagingLineToFields(acc);
    if (isExcludedFromEquipmentBase(fields)) continue;
    into.sum += Math.max(0, equipmentBaseAmountFromLine(fields));
  }
}

/**
 * D-Tools hierarchy: sum visible equipment only (hidden systems/rooms excluded).
 * Does not include service / percentage / discount / cable buckets.
 */
export function sumVisibleEquipmentCostFromSystems(
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): number {
  const visible = filterVisibleDtoolsSystems(systems, displaySettings, { audience: "customer" }).map(
    (entry) => entry.system,
  );
  const into = { sum: 0 };
  for (const system of visible) {
    for (const room of system.rooms) {
      for (const block of room.primaries) addPrimaryEquipmentTotal(block, into);
    }
    for (const block of system.systemLevelPrimaries) addPrimaryEquipmentTotal(block, into);
  }
  return roundMoney(into.sum);
}

/** Apply percentage pricing to flat lines; returns new array (immutable). */
export function applyEquipmentPercentagePricingToFlatLines<T extends FlatEquipmentLine>(
  lines: T[],
): T[] {
  const equipmentBase = sumFlatVisibleEquipmentCost(lines);
  return lines.map((line) => {
    if (!isEquipmentPercentageLine(line)) return line;
    const amount = computeEquipmentPercentageAmount(equipmentBase, line.percentageOfEquipment);
    const qty = Math.max(1, Math.floor(moneyNumber(line.quantity) || 1));
    return {
      ...line,
      quantity: qty,
      unitPrice: amount,
      lineTotal: amount,
    };
  });
}

export type StagingLineWithPercentage = DtoolsStagingLine & {
  pricingMethod?: EquipmentPricingMethod | string | null;
  percentageOfEquipment?: number | null;
};

/**
 * Apply percentage pricing on D-Tools staging/revise lines using hierarchy visibility.
 * Equipment base uses the same visibility rules as customer-facing deal totals.
 */
export function applyEquipmentPercentagePricingToStagingLines(
  lines: StagingLineWithPercentage[],
  systems: DtoolsReviewSystem[],
  displaySettings?: DtoolsDisplaySettings,
): StagingLineWithPercentage[] {
  const equipmentBase = sumVisibleEquipmentCostFromSystems(systems, displaySettings);
  return lines.map((line) => {
    if (
      !isEquipmentPercentagePricingAllowed({
        brand: line.manufacturer,
        pricingMethod: line.pricingMethod,
      })
    ) {
      return line;
    }
    const amount = computeEquipmentPercentageAmount(equipmentBase, line.percentageOfEquipment);
    return {
      ...line,
      quantity: Math.max(1, line.quantity || 1),
      unitPrice: amount,
      lineTotal: amount,
    };
  });
}

/**
 * Guard used in tests: percentage lines must not appear in the equipment base.
 */
export function assertNoRecursivePercentageBase(lines: FlatEquipmentLine[]): void {
  const base = sumFlatVisibleEquipmentCost(lines);
  for (const line of lines) {
    if (!isEquipmentPercentageLine(line)) continue;
    // Recompute base without this line's total if it were wrongly included — must match.
    const without = lines.filter((l) => l !== line);
    const baseWithout = sumFlatVisibleEquipmentCost(without);
    if (Math.abs(base - baseWithout) > 0.005) {
      throw new Error("Percentage line leaked into equipment base (recursive pricing).");
    }
  }
  void base;
}

/** Catalogue eligibility exception: Clarity + percentage_of_equipment may be labour-named. */
export function isClarityPercentageCatalogueProduct(input: {
  brand?: string | null;
  manufacturer?: string | null;
  pricingMethod?: unknown;
}): boolean {
  return isEquipmentPercentagePricingAllowed({
    brand: input.brand ?? input.manufacturer,
    pricingMethod: input.pricingMethod,
  });
}

function parseBucketFromRawText(rawText: unknown): string | null {
  if (typeof rawText !== "string" || !rawText.trim()) return null;
  try {
    const parsed = JSON.parse(rawText) as { bucket?: unknown };
    return typeof parsed.bucket === "string" ? parsed.bucket : null;
  } catch {
    return null;
  }
}

/** Apply % pricing to proposal create/revise item payloads (retail + D-Tools flat rows). */
export function applyEquipmentPercentagePricingToProposalItems<
  T extends {
    brand?: string | null;
    name?: string | null;
    model?: string | null;
    description?: string;
    category?: string | null;
    rawText?: string | null;
    unitPrice: number;
    qty: number;
    lineTotal?: number;
    pricingMethod?: string | null;
    percentageOfEquipment?: number | null;
  },
>(items: T[]): T[] {
  const flat: FlatEquipmentLine[] = items.map((it) => {
    const lineTotal =
      it.lineTotal != null && Number.isFinite(Number(it.lineTotal))
        ? Number(it.lineTotal)
        : Number(it.unitPrice) * Number(it.qty);
    return {
      brand: it.brand,
      name: it.name,
      model: it.model,
      description: it.description,
      category: it.category,
      bucket: parseBucketFromRawText(it.rawText),
      pricingMethod: it.pricingMethod,
      percentageOfEquipment: it.percentageOfEquipment,
      lineTotal,
      unitPrice: it.unitPrice,
      quantity: it.qty,
    };
  });
  const applied = applyEquipmentPercentagePricingToFlatLines(flat);
  return items.map((it, i) => {
    const next = applied[i]!;
    if (!isEquipmentPercentageLine(flat[i]!)) return it;
    return {
      ...it,
      unitPrice: next.unitPrice ?? next.lineTotal,
      qty: Math.max(1, Math.floor(moneyNumber(next.quantity) || 1)),
      lineTotal: next.lineTotal,
      pricingMethod: EQUIPMENT_PRICING_METHOD_PERCENTAGE,
      percentageOfEquipment: normalizeDefaultPercentage(it.percentageOfEquipment),
    };
  });
}

export type ProposalDbItemForPercentagePricing = {
  id?: number;
  brand?: string | null;
  name?: string | null;
  model?: string | null;
  category?: string | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  pricingMethod?: string | null;
  percentageOfEquipment?: string | number | null;
  unitPriceSnapshot?: string | number | null;
  lineTotal?: string | number | null;
  quantity?: number | null;
};

/**
 * Apply % pricing to persisted proposal_items rows.
 * Prefer D-Tools hierarchy equipment base when systems are provided (room/system hide aware).
 */
export function applyEquipmentPercentagePricingToDbItems<T extends ProposalDbItemForPercentagePricing>(
  items: T[],
  options?: {
    systems?: DtoolsReviewSystem[];
    displaySettings?: DtoolsDisplaySettings;
  },
): T[] {
  const hasPercentage = items.some((item) =>
    isEquipmentPercentagePricingAllowed({
      brand: item.brand,
      pricingMethod: item.pricingMethod,
    }),
  );
  if (!hasPercentage) return items;

  const equipmentBase =
    options?.systems != null
      ? sumVisibleEquipmentCostFromSystems(options.systems, options.displaySettings)
      : sumFlatVisibleEquipmentCost(
          items.map((item) => ({
            brand: item.brand,
            name: item.name,
            model: item.model,
            category: item.category,
            description: item.descriptionSnapshot,
            bucket: parseBucketFromRawText(item.rawText),
            pricingMethod: item.pricingMethod,
            percentageOfEquipment: normalizeDefaultPercentage(item.percentageOfEquipment),
            lineTotal: moneyNumber(item.lineTotal),
            unitPrice: moneyNumber(item.unitPriceSnapshot),
            quantity: Math.max(1, Math.round(moneyNumber(item.quantity) || 1)),
          })),
        );

  return items.map((item) => {
    if (
      !isEquipmentPercentagePricingAllowed({
        brand: item.brand,
        pricingMethod: item.pricingMethod,
      })
    ) {
      return item;
    }
    const amount = computeEquipmentPercentageAmount(
      equipmentBase,
      item.percentageOfEquipment != null ? Number(item.percentageOfEquipment) : null,
    );
    return {
      ...item,
      quantity: 1,
      unitPriceSnapshot: amount,
      lineTotal: amount,
    };
  });
}

/**
 * Prefer hierarchy-visible equipment base when D-Tools systems are available;
 * otherwise fall back to flat equipment sum.
 */
export function applyEquipmentPercentagePricingToProposalItemsWithVisibility<
  T extends {
    brand?: string | null;
    name?: string | null;
    model?: string | null;
    description?: string;
    category?: string | null;
    rawText?: string | null;
    unitPrice: number;
    qty: number;
    lineTotal?: number;
    pricingMethod?: string | null;
    percentageOfEquipment?: number | null;
  },
>(
  items: T[],
  options?: {
    systems?: DtoolsReviewSystem[];
    displaySettings?: DtoolsDisplaySettings;
  },
): T[] {
  if (!options?.systems) {
    return applyEquipmentPercentagePricingToProposalItems(items);
  }
  const dbShaped = items.map((it, idx) => ({
    id: idx + 1,
    brand: it.brand,
    name: it.name,
    model: it.model,
    category: it.category,
    rawText: it.rawText,
    descriptionSnapshot: it.description,
    pricingMethod: it.pricingMethod,
    percentageOfEquipment: it.percentageOfEquipment,
    unitPriceSnapshot: it.unitPrice,
    lineTotal: it.lineTotal ?? it.unitPrice * it.qty,
    quantity: it.qty,
  }));
  const priced = applyEquipmentPercentagePricingToDbItems(dbShaped, options);
  return items.map((it, i) => {
    const next = priced[i]!;
    if (
      !isEquipmentPercentagePricingAllowed({
        brand: it.brand,
        pricingMethod: it.pricingMethod,
      })
    ) {
      return it;
    }
    return {
      ...it,
      unitPrice: moneyNumber(next.unitPriceSnapshot),
      qty: Math.max(1, Math.round(moneyNumber(next.quantity) || 1)),
      lineTotal: moneyNumber(next.lineTotal),
      pricingMethod: EQUIPMENT_PRICING_METHOD_PERCENTAGE,
      percentageOfEquipment: normalizeDefaultPercentage(it.percentageOfEquipment),
    };
  });
}
